<template>
  <div class="project68-batch-processor">
    <div class="header">
      <h2>🏗️ {{ projectDisplayName }} Legacy Document Processing</h2>
      <p class="subtitle">Process legacy documents with comprehensive AI analysis</p>
    </div>

    <!-- Status Overview -->
    <div v-if="statusData" class="status-overview">
      <div class="status-cards">
        <div class="status-card">
          <div class="status-number">{{ statusData.total_documents }}</div>
          <div class="status-label">Total Documents</div>
        </div>
        <div class="status-card">
          <div class="status-number">{{ statusData.processed_documents }}</div>
          <div class="status-label">Processed</div>
        </div>
        <div class="status-card">
          <div class="status-number">{{ statusData.processing_percentage }}%</div>
          <div class="status-label">Complete</div>
        </div>
      </div>
    </div>

    <!-- Processing Controls -->
    <div class="processing-controls">
      <div class="control-group">
        <label for="start-date">
          <i class="fas fa-calendar-alt"></i>
          Start Date:
        </label>
        <input 
          id="start-date"
          v-model="startDate" 
          type="date" 
          :disabled="isProcessing"
          class="date-input"
        />
      </div>
      
      <div class="control-group">
        <label for="end-date">
          <i class="fas fa-calendar-alt"></i>
          End Date:
        </label>
        <input 
          id="end-date"
          v-model="endDate" 
          type="date" 
          :disabled="isProcessing"
          class="date-input"
        />
      </div>
      
      <div class="control-group">
        <label for="sample-size">Sample Size:</label>
        <input 
          id="sample-size"
          v-model.number="sampleSize" 
          type="number" 
          min="1" 
          max="5000" 
          :disabled="isProcessing"
        />
        <small>Max documents to process</small>
      </div>
      
      <div class="control-group">
        <label for="batch-size">Batch Size:</label>
        <input 
          id="batch-size"
          v-model.number="batchSize" 
          type="number" 
          min="1" 
          max="50" 
          :disabled="isProcessing"
        />
        <small>Documents per batch</small>
      </div>
      
      <div class="control-group">
        <label for="max-workers">Parallel Workers:</label>
        <input 
          id="max-workers"
          v-model.number="maxWorkers" 
          type="number" 
          min="1" 
          max="10" 
          :disabled="isProcessing"
          title="Number of documents to process simultaneously (3-5 recommended)"
        />
        <small>Higher = faster but more resource intensive</small>
      </div>
      
      <div class="control-group full-width">
        <label class="checkbox-label">
          <input 
            v-model="skipProcessed" 
            type="checkbox"
            :disabled="isProcessing"
          />
          Skip already processed documents
        </label>
        <small>Uncheck to force reprocess all documents in date range</small>
      </div>
      
      <button 
        @click="startProcessing" 
        :disabled="isProcessing || !startDate || !endDate"
        class="start-button"
      >
        <i class="fas" :class="isProcessing ? 'fa-spinner fa-spin' : 'fa-play'"></i>
        {{ isProcessing ? 'Processing...' : 'Start Processing' }}
      </button>
    </div>

    <!-- Progress Display -->
    <div v-if="currentBatch" class="progress-section">
      <h3>Processing Progress</h3>
      
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: progressPercentage + '%' }"
        ></div>
      </div>
      
      <div class="progress-info">
        <span>{{ currentBatch.current_document }} / {{ currentBatch.total_documents }} documents</span>
        <span v-if="currentBatch.current_file" class="current-file">
          Processing: {{ currentBatch.current_file }}
        </span>
      </div>
      
      <div class="status-badge" :class="currentBatch.status">
        {{ getStatusLabel(currentBatch.status) }}
      </div>
    </div>

    <!-- Results -->
    <div v-if="currentBatch && currentBatch.processed_documents.length > 0" class="results-section">
      <div class="results-header">
        <h3>Processing Results</h3>
        
        <div class="results-summary">
          <div class="result-stat">
            <span class="stat-number">{{ completedCount }}</span>
            <span class="stat-label">Completed</span>
          </div>
          <div class="result-stat">
            <span class="stat-number">{{ skippedCount }}</span>
            <span class="stat-label">Skipped</span>
          </div>
          <div class="result-stat">
            <span class="stat-number">{{ errorCount }}</span>
            <span class="stat-label">Errors</span>
          </div>
        </div>
      </div>
      
      <div class="results-list-container">
        <div class="results-list">
        <div 
          v-for="result in currentBatch.processed_documents" 
          :key="result.document_id || result.filename"
          class="result-item"
          :class="result.status"
        >
          <div class="result-icon">
            <span v-if="result.status === 'completed'">✅</span>
            <span v-else-if="result.status === 'skipped'">⏭️</span>
            <span v-else-if="result.status === 'error'">❌</span>
          </div>
          <div class="result-details">
            <div class="result-filename">{{ result.document_name || result.filename }}</div>
            <div class="result-vendor" v-if="result.vendor_name">{{ result.vendor_name }}</div>
            <div class="result-reason" v-if="result.reason">{{ result.reason }}</div>
            <div class="result-error" v-if="result.error">{{ result.error }}</div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Errors -->
    <div v-if="currentBatch && currentBatch.errors.length > 0" class="errors-section">
      <h3>⚠️ Errors</h3>
      <div class="error-list">
        <div v-for="error in currentBatch.errors" :key="error" class="error-item">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'Project68BatchProcessor',
  data() {
    return {
      startDate: '2024-11-01',  // Default to Nov 1, 2024
      endDate: '2024-12-31',    // Default to Dec 31, 2024
      sampleSize: 500,
      batchSize: 10,
      maxWorkers: 3,  // Parallel processing workers
      skipProcessed: true,  // Default to skipping already processed
      isProcessing: false,
      currentBatch: null,
      statusData: null,
      pollInterval: null
    };
  },
  
  computed: {
    progressPercentage() {
      if (!this.currentBatch || !this.currentBatch.total_documents) return 0;
      return Math.round((this.currentBatch.current_document / this.currentBatch.total_documents) * 100);
    },
    
    completedCount() {
      return this.currentBatch.processed_documents.filter(r => r.status === 'completed').length;
    },
    
    skippedCount() {
      return this.currentBatch.processed_documents.filter(r => r.status === 'skipped').length;
    },
    
    errorCount() {
      return this.currentBatch.processed_documents.filter(r => r.status === 'error').length;
    },
    
    projectDisplayName() {
      // Get project name from status data (returned by backend based on user's project_id)
      if (this.statusData?.project_name) {
        // Extract just the name part before the parenthetical mapping info
        const name = this.statusData.project_name;
        // If it contains a parenthetical, just use the first part
        return name.split(' (')[0] || name;
      }
      return 'Legacy Project';
    }
  },
  
  mounted() {
    this.loadStatus();
  },
  
  beforeUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },
  
  methods: {
    async loadStatus() {
      try {
        const response = await api.get('/project68/status/');
        this.statusData = response.data;
      } catch (error) {
        console.error('Error loading legacy project status:', error);
        if (error.response?.status === 400) {
          this.$emit('error', 'User does not have a project_id set. Please contact administrator.');
        }
      }
    },
    
    async startProcessing() {
      try {
        this.isProcessing = true;
        
        // Format date range for backend
        const dateRange = `${this.startDate} to ${this.endDate}`;
        
        const response = await api.post('/project68/batch/', {
          sample_size: this.sampleSize,
          batch_size: this.batchSize,
          max_workers: this.maxWorkers,
          date_range: dateRange,
          skip_processed: this.skipProcessed
        });
        
        const batchId = response.data.batch_id;
        this.startPolling(batchId);
        
        this.$emit('success', `Started processing ${this.sampleSize} documents from ${this.startDate} to ${this.endDate}`);
        
      } catch (error) {
        console.error('Error starting processing:', error);
        this.isProcessing = false;
        this.$emit('error', error.response?.data?.error || 'Failed to start processing');
      }
    },
    
    startPolling(batchId) {
      this.pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/project68/batch/status/?batch_id=${batchId}`);
          this.currentBatch = response.data;
          
          if (response.data.status === 'completed' || response.data.status === 'error') {
            this.isProcessing = false;
            clearInterval(this.pollInterval);
            this.pollInterval = null;
            
            // Refresh status
            this.loadStatus();
            
            if (response.data.status === 'completed') {
              this.$emit('success', `Processing completed! Processed ${response.data.processed_documents.length} documents`);
            } else {
              this.$emit('error', 'Processing completed with errors');
            }
          }
          
        } catch (error) {
          console.error('Error polling batch status:', error);
          this.isProcessing = false;
          clearInterval(this.pollInterval);
          this.pollInterval = null;
        }
      }, 2000); // Poll every 2 seconds
    },
    
    getStatusLabel(status) {
      const labels = {
        'initializing': 'Initializing...',
        'fetching_files': 'Fetching Files...',
        'processing': 'Processing Documents...',
        'completed': 'Completed',
        'error': 'Error'
      };
      return labels[status] || status;
    }
  }
};
</script>

<style scoped>
.project68-batch-processor {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.subtitle {
  color: #7f8c8d;
  font-size: 16px;
}

.status-overview {
  margin-bottom: 30px;
}

.status-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.status-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #e1e8ed;
}

.status-number {
  font-size: 2em;
  font-weight: bold;
  color: #3498db;
  margin-bottom: 5px;
}

.status-label {
  color: #7f8c8d;
  font-size: 14px;
}

.processing-controls {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  align-items: end;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.control-group.full-width {
  grid-column: 1 / -1;
}

.control-group label {
  font-weight: 500;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.control-group label i {
  color: #667eea;
}

.control-group input[type="number"],
.control-group input[type="date"] {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  min-width: 120px;
}

.date-input {
  font-size: 0.95rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

.control-group small {
  color: #7f8c8d;
  font-size: 0.85rem;
  margin-top: -3px;
}

.start-button {
  background: #27ae60;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  grid-column: span 2;
}

.start-button:hover:not(:disabled) {
  background: #219a52;
}

.start-button:disabled {
  background: #95a5a6;
  cursor: not-allowed;
}

.start-button i {
  font-size: 1rem;
}

.progress-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.progress-bar {
  width: 100%;
  height: 20px;
  background: #ecf0f1;
  border-radius: 10px;
  overflow: hidden;
  margin: 15px 0;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3498db, #2ecc71);
  transition: width 0.3s ease;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.current-file {
  font-style: italic;
  color: #7f8c8d;
  font-size: 14px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  margin-top: 10px;
}

.status-badge.initializing,
.status-badge.fetching_files,
.status-badge.processing {
  background: #3498db;
  color: white;
}

.status-badge.completed {
  background: #27ae60;
  color: white;
}

.status-badge.error {
  background: #e74c3c;
  color: white;
}

.results-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.results-header {
  margin-bottom: 20px;
}

.results-header h3 {
  margin-bottom: 15px;
  color: #2c3e50;
}

.results-summary {
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  justify-content: space-around;
}

.result-stat {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 1.5em;
  font-weight: bold;
  color: #2c3e50;
}

.stat-label {
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
}

.results-list-container {
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #e1e8ed;
  border-radius: 6px;
  padding: 10px;
  background: #fafbfc;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-bottom: 1px solid #ecf0f1;
}

.result-item:last-child {
  border-bottom: none;
}

.result-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.result-details {
  flex: 1;
}

.result-filename {
  font-weight: 500;
  color: #2c3e50;
}

.result-vendor {
  font-size: 14px;
  color: #7f8c8d;
}

.result-reason {
  font-size: 12px;
  color: #f39c12;
  font-style: italic;
}

.result-error {
  font-size: 12px;
  color: #e74c3c;
}

.errors-section {
  background: #fdf2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 20px;
}

.errors-section h3 {
  color: #dc2626;
  margin-bottom: 15px;
}

.error-list {
  max-height: 200px;
  overflow-y: auto;
}

.error-item {
  background: white;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
  color: #dc2626;
  font-family: monospace;
  font-size: 14px;
}
</style>
